import { Form, Formik, FormikHelpers } from 'formik';
import React, { useMemo } from 'react';
import { SchemaOf } from 'yup';
import allCountries from 'country-region-data/data.json';
import * as Yup from 'yup';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { Workshop } from '../../../models/auto-generated-strapi-types/types/Workshop';
import { FormErrorMessageTypes, Pages } from '../../../models/domain/enum';
import { ContentWrapper } from '../../../styles/shared';
import {
  ContentForm,
  Description,
  FormBody,
  FormRowGroup,
  SubmitButton,
  Title,
} from './FormWorkshop.style';
import { SnackbarActions, useSnackbar } from '../../../stores/snackbar';
import Input, { InputStyleType } from '../../../components/form/input/Input';
import Field from '../../../components/form/field/Field';
import Select, { SelectType } from '../../../components/form/select/Select';
import { Errors, TextSize } from '../../../models/enum';
import { ButtonStyle } from '../../../components/button/Button';
import { arrayProvince } from '../../../utils/province';
import Radio from '../../../components/form/radio/Radio';
import Checkbox, {
  CheckboxType,
} from '../../../components/form/checkbox/Checkbox';
import { Slug } from '../../../models/domain/interfaces';
import { getWorkshop, getWorkshops } from '../../../utils/api';
import parseISO from 'date-fns/parseISO';
import { normalizeAttributesWorkshops } from '../../../utils/workshops';
import Markdown from '../../../components/markdown/Markdown';
import { WorkshopFormComponent } from '../../../models/auto-generated-strapi-types/types/components/WorkshopFormComponent';
import { getPersonalizationAllAttributes } from '../../../utils/style';
import { Logger } from 'corporate-utils';
import { formSubmitted } from '../../../utils/tagManager';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { UserContext } from '../../../models/interface';
import { getUseAlternativeCaptcha } from '../../../utils/captcha';

interface FormWorkshopProps {
  workshop: Workshop;
  form: WorkshopFormComponent;
  workshopList: Workshop[];
}

interface WorkshopFormikValues {
  email: string;
  firstName: string;
  lastName: string;
  profession: string;
  province?: string;
  iscrittoApe: string[];
  courses: string[];
  state: string;
  marketing: string;
  profiling: string;
}

type WorkshopValidationSchema = SchemaOf<WorkshopFormikValues>;

const FormWorkshop: React.FC<{
  data: FormWorkshopProps;
  translations: any;
  userContext: UserContext;
}> = ({ data, translations, userContext }) => {
  const { dispatch } = useSnackbar();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const section = 'workshop';
  const hCaptchaRef = React.useRef<HCaptcha>(null);
  const useAlternativeCaptcha = getUseAlternativeCaptcha(userContext?.country);

  const initialValues = (): WorkshopFormikValues => ({
    email: data?.form?.emailShow ? '' : 'x',
    firstName: data?.form?.nameShow ? '' : 'x',
    lastName: data?.form?.lastnameShow ? '' : 'x',
    profession: data?.form?.nameShow ? '' : 'x',
    province: data?.form?.nameShow ? '' : 'x',
    iscrittoApe: [],
    courses: !data?.form?.coursesShow
      ? ['x']
      : data?.workshop?.attributes?.valueForForm
        ? [data.workshop.attributes.valueForForm]
        : [
          data?.workshop?.attributes?.title
            ? data?.workshop?.attributes?.title
            : 'not found',
        ],
    state: data?.form?.stateDefault || '',
    marketing: '',
    profiling: '',
  });

  const validationSchema = (): WorkshopValidationSchema => {
    const formatErrorMessage = (
      fieldName: string,
      errorType: FormErrorMessageTypes = FormErrorMessageTypes.REQUIRED,
      values?: any[]
    ) => {
      let message = `${fieldName} ${translations?.pages?.workshop?.errors?.[errorType]}`;

      if (errorType === FormErrorMessageTypes.ONE_OF) {
        if (!values) throw new Error('Argument values required');
        message += `: ${values?.join(',')}`;
      }

      return message;
    };

    return Yup.object({
      firstName: Yup.string().required(
        formatErrorMessage(data?.form?.nameLabel || 'name')
      ),
      lastName: Yup.string().required(
        formatErrorMessage(data?.form?.lastnameLabel || 'lastname')
      ),
      email: Yup.string()
        .required(formatErrorMessage(data?.form?.emailLabel || 'email'))
        .email(
          formatErrorMessage(
            data?.form?.emailLabel || 'email',
            FormErrorMessageTypes.EMAIL
          )
        ),
      profession: Yup.string().required(
        formatErrorMessage(data?.form?.professionLabel || 'profession')
      ),
      province: Yup.string(),
      state: Yup.string().required(
        formatErrorMessage(data?.form?.stateLabel || 'state')
      ),
      iscrittoApe: Yup.array(),
      courses: Yup.array().min(
        1,
        formatErrorMessage(data?.form?.coursesLabel || 'courses')
      ),
      profiling: Yup.string().required(
        formatErrorMessage(data?.form?.profilazioneLabel || 'profiling')
      ),
      marketing: Yup.string().required(
        formatErrorMessage(data?.form?.marketingLabel || 'marketing')
      ),
    });
  };

  const hubspotBodyAdapter = (values: WorkshopFormikValues) => {
    const body = {
      submittedAt: Date.now(),
      fields: [
        {
          name: 'email',
          value: values?.email,
        },
        {
          name: 'firstname',
          value: values?.firstName,
        },
        {
          name: 'lastname',
          value: values?.lastName,
        },
        {
          name: 'provincia_di_appartenenza',
          value: values?.province,
        },
        {
          name: 'professione_contatto',
          value: values?.profession,
        },
        {
          name: 'corsielencoperprenotazione',
          value: values?.courses
            ?.filter?.((course: string) => course !== 'not found')
            ?.map?.((c) => c)
            .join(';'),
        },
        {
          name: 'iscrittoApe',
          value: values?.iscrittoApe?.length > 0 ? true : false,
        },
        {
          name: 'consenso_marketing_studio_portolano',
          value: values?.marketing,
        },
        {
          name: 'consenso_profilazione_portolano',
          value: values?.profiling,
        },
      ],
    };
    if (data?.workshop?.attributes?.registration) {
      const date = parseISO(
        (data?.workshop?.attributes as any)?.originalDate || ''
      );

      let stringDate = 'xxxxxx';
      if (date) stringDate = `${date?.getFullYear()}${date?.getMonth() + 1}`;
      body.fields.push({
        name: 'anno_mese_evento',
        value: stringDate,
      });
    }

    if (data?.workshop?.attributes?.registration) {
      body.fields.push({
        name: 'stato',
        value: values?.state,
      });
    }

    return body;
  };

  const submitForm = async (
    values: WorkshopFormikValues,
    { resetForm }: FormikHelpers<WorkshopFormikValues>
  ) => {
    try {
      let token;

      if (useAlternativeCaptcha) {
        token = await hCaptchaRef?.current
          ?.execute({ async: true })
          .then(({ response }) => {
            return response;
          })
          .catch((err) => {
            console.error(err);
          });
      } else {
        token = (await executeRecaptcha?.('workshopForm')) || '';
      }

      const headers = new Headers();
      headers.set('Content-Type', 'application/json');

      const resp = await fetch('/api/workshop-hubspot', {
        headers,
        method: 'POST',
        body: JSON.stringify({
          formId: data?.workshop?.attributes?.registration
            ? data?.form?.idHubspotRegistration
            : data?.form?.idHubspotPreRegistration,
          data: hubspotBodyAdapter(values),
          action: 'workshopForm',
          captchaToken: {
            token,
            alternativeCaptcha: useAlternativeCaptcha,
          },
        }),
      });
      const isSuccess = resp.status === 200;
      const dataRes: {
        result: {
          message?: string;
          inlineMessage?: string;
        };
        formGuid?: string;
      } = await resp.json();
      dispatch({
        type: SnackbarActions.SHOW,
        success: isSuccess,
        message: isSuccess
          ? dataRes?.result?.inlineMessage?.replace(/(<([^>]+)>)/gi, '')
          : dataRes?.result?.message || translations?.pages?.errors?.generic,
      });

      if (isSuccess) {
        dataRes?.formGuid && formSubmitted(dataRes?.formGuid);
        resetForm();
      }
    } catch (error) {
      const err = error as Error;
      Logger.error(err);
      dispatch({
        type: SnackbarActions.SHOW,
        success: false,
        message: translations?.pages?.errors?.generic,
      });
    }
  };

  const professionOptions = useMemo(() => {
    const options = data?.form?.professionItems
      ? data?.form?.professionItems?.map((item) => ({
        label: item?.label,
        value: item?.value,
      }))
      : [];
    options.unshift({
      label: translations?.pages?.workshop?.options?.professionDefault,
      value: '',
    });
    return options;
  }, [
    data?.form?.professionItems,
    translations?.pages?.workshop?.options?.professionDefault,
  ]);

  const provinceOptions = useMemo(() => {
    const options = arrayProvince?.map?.((item) => ({
      label: item,
      value: item?.toUpperCase?.(),
    }));
    options.unshift({
      label: translations?.pages?.workshop?.options?.provinceDefault,
      value: '',
    });
    return options;
  }, [translations?.pages?.workshop?.options?.provinceDefault]);

  const stateOptions = useMemo(() => {
    const options = allCountries
      .map(
        (
          country: any
        ):
          | {
              label: string;
              value: string;
            }
          | undefined => {
          if (translations?.states?.[country.countryShortCode]) {
            return {
              label: translations?.states?.[country.countryShortCode],
              value: country.countryName,
            };
          }
        }
      )
      .filter((noUndefined) => !!noUndefined);

    options.unshift({
      label: translations?.pages?.workshop?.options?.stateDefault,
      value: '',
    });

    return options;
  }, [translations]);

  const coursesOptions = useMemo(
    () =>
      data?.workshopList
        ?.filter((w) => w?.attributes?.title && w?.attributes?.valueForForm)
        .map((item) => ({
          label: item?.attributes?.title,
          value: item?.attributes?.valueForForm,
        })),
    [data?.workshopList]
  );

  const profilingOptions = useMemo(
    () => [
      {
        label: translations?.pages?.workshop?.options?.yes,
        value: 'profilazione_consenso',
      },
      {
        label: translations?.pages?.workshop?.options?.no,
        value: 'profilazione_rifiuto',
      },
    ],
    [
      translations?.pages?.workshop?.options?.no,
      translations?.pages?.workshop?.options?.yes,
    ]
  );

  const marketingOptions = useMemo(
    () => [
      {
        label: translations?.pages?.workshop?.options?.yes,
        value: 'marketing_consenso',
      },
      {
        label: translations?.pages?.workshop?.options?.no,
        value: 'marketing_rifiuto',
      },
    ],
    [
      translations?.pages?.workshop?.options?.no,
      translations?.pages?.workshop?.options?.yes,
    ]
  );

  const iscrittoApeOptions = useMemo(
    () => [
      {
        label: data?.form?.iscrittoapeLabel,
        value: 'iscrittoApe',
      },
    ],
    [data?.form?.iscrittoapeLabel]
  );

  const title = data?.workshop?.attributes?.registration
    ? data?.workshop?.attributes?.formMainTitleRegistration ??
      (data?.form?.defaultMainTitleRegistration || undefined)
    : data?.workshop?.attributes?.formMainTitle ??
      (data?.form?.defaultMainTitle || undefined);

  const description = data?.workshop?.attributes?.registration
    ? data?.workshop?.attributes?.formMainDescriptionRegistration ??
      (data?.form?.defaultMainDescriptionRegistration || undefined)
    : data?.workshop?.attributes?.formMainDescription ??
      (data?.form?.defaultMainDescription || undefined);

  return (
    <ContentWrapper>
      <Formik
        initialValues={initialValues()}
        validationSchema={(): WorkshopValidationSchema => validationSchema()}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={submitForm}
      >
        {() => {
          return (
            <Form>
              <ContentForm>
                {title && (
                  <Title
                    personalization={getPersonalizationAllAttributes(
                      data?.workshop?.attributes?.personalizationForm,
                      'title'
                    )}
                  >
                    <Markdown>{title}</Markdown>
                  </Title>
                )}
                {description && (
                  <Description
                    personalization={getPersonalizationAllAttributes(
                      data?.workshop?.attributes?.personalizationForm,
                      'description'
                    )}
                  >
                    <Markdown>{description}</Markdown>
                  </Description>
                )}
                <FormBody>
                  {data?.form?.emailShow && (
                    <FormRowGroup>
                      <Field
                        label={data?.form?.emailLabel}
                        name="email"
                        section={section}
                        placeholder={
                          data?.form?.emailPlaceholder || data?.form?.emailLabel
                        }
                        styleType={InputStyleType.workshop}
                        component={Input}
                      />
                    </FormRowGroup>
                  )}
                  {data?.form?.nameShow && (
                    <FormRowGroup>
                      <Field
                        label={data?.form?.nameLabel}
                        name="firstName"
                        section={section}
                        placeholder={
                          data?.form?.namePlaceholder || data?.form?.nameLabel
                        }
                        styleType={InputStyleType.workshop}
                        component={Input}
                      />
                    </FormRowGroup>
                  )}
                  {data?.form?.lastnameShow && (
                    <FormRowGroup>
                      <Field
                        label={data?.form?.lastnameLabel}
                        name="lastName"
                        section={section}
                        placeholder={
                          data?.form?.lastnamePlaceholder ||
                          data?.form?.lastnameLabel
                        }
                        styleType={InputStyleType.workshop}
                        component={Input}
                      />
                    </FormRowGroup>
                  )}
                  {data?.form?.professionShow && (
                    <FormRowGroup>
                      <Field
                        label={data?.form?.professionLabel}
                        name="profession"
                        section={section}
                        selectType={SelectType.workshop}
                        options={professionOptions}
                        textSize={TextSize.workshop}
                        component={Select}
                      />
                    </FormRowGroup>
                  )}
                  {data?.form?.provinceShow && (
                    <FormRowGroup>
                      <Field
                        label={data?.form?.provinceLabel}
                        name="province"
                        section={section}
                        selectType={SelectType.workshop}
                        options={provinceOptions}
                        textSize={TextSize.workshop}
                        component={Select}
                      />
                    </FormRowGroup>
                  )}
                  {data?.form?.stateShow && (
                    <FormRowGroup>
                      <Field
                        label={data?.form?.stateLabel}
                        name="state"
                        section={section}
                        selectType={SelectType.workshop}
                        options={stateOptions}
                        textSize={TextSize.workshop}
                        component={Select}
                        placeholder={data?.form?.stateDefault}
                      />
                    </FormRowGroup>
                  )}
                  {data?.form?.iscrittoapeShow && (
                    <FormRowGroup>
                      <Field
                        label={data?.form?.iscrittoapeLabel}
                        name="iscrittoApe"
                        section={section}
                        checkboxType={CheckboxType.workshop}
                        options={iscrittoApeOptions}
                        textSize={TextSize.workshop}
                        component={Checkbox}
                      />
                    </FormRowGroup>
                  )}
                  {data?.form?.coursesShow &&
                    !data?.workshop?.attributes?.registration && (
                    <FormRowGroup>
                      <Field
                        label={data?.form?.coursesLabel}
                        name="courses"
                        section={section}
                        checkboxType={CheckboxType.workshop}
                        options={coursesOptions}
                        textSize={TextSize.workshop}
                        component={Checkbox}
                      />
                    </FormRowGroup>
                  )}
                  {data?.form?.marketingShow && (
                    <FormRowGroup>
                      <Field
                        label={data?.form?.marketingLabel}
                        subText={data?.form?.marketingText}
                        name="marketing"
                        section={section}
                        radioType={SelectType.workshop}
                        options={marketingOptions}
                        textSize={TextSize.workshop}
                        component={Radio}
                      />
                    </FormRowGroup>
                  )}
                  {data?.form?.profilazioneShow && (
                    <FormRowGroup>
                      <Field
                        label={data?.form?.profilazioneLabel}
                        subText={data?.form?.profilazioneText}
                        name="profiling"
                        section={section}
                        radioType={SelectType.workshop}
                        options={profilingOptions}
                        textSize={TextSize.workshop}
                        component={Radio}
                      />
                    </FormRowGroup>
                  )}
                  <FormRowGroup>
                    <SubmitButton
                      text={translations?.pages?.contactForm?.submit}
                      styleBtn={ButtonStyle.contactFormPage}
                      type="submit"
                    />
                    {useAlternativeCaptcha && (
                      <HCaptcha
                        sitekey={
                          process.env.NEXT_PUBLIC_H_CAPTCHA_SITE_KEY || ''
                        }
                        size="invisible"
                        ref={hCaptchaRef}
                      />
                    )}
                  </FormRowGroup>
                </FormBody>
              </ContentForm>
            </Form>
          );
        }}
      </Formik>
    </ContentWrapper>
  );
};

export const getServerSideProps = async (
  section: any,
  translations: any,
  slugAnalyzed: Slug
) => {
  switch (slugAnalyzed.type) {
    case Pages.WORKSHOP:
      {
        const workshop =
          slugAnalyzed?.workshopSlug &&
          (await getWorkshop({
            slug: slugAnalyzed?.workshopSlug,
            locale: slugAnalyzed?.locale,
            dateFormat: translations?.date?.workshop?.format,
          }));

        const workshops = await getWorkshops({
          locale: slugAnalyzed?.locale,
          pageSize: 12,
          sort: ['date:desc'],
          datesFormat: translations?.date?.workshop?.format,
          filters: {
            registration: {
              $eq: false,
            },
          },
        });

        section.workshopList = normalizeAttributesWorkshops(
          workshops?.data,
          translations,
          slugAnalyzed?.locale
        );

        section.form =
          workshop?.attributes?.workshopForm?.data?.attributes?.form;
        section.workshop = workshop;
      }
      break;
    case Pages.WORKSHOPS:
      {
        const workshops = await getWorkshops({
          locale: slugAnalyzed?.locale,
          pageSize: 12,
          sort: ['date:desc'],
          datesFormat: translations?.date?.workshop?.format,
          filters: {
            registration: {
              $eq: false,
            },
          },
        });

        const tempSection = { ...section };

        section.form = tempSection;
        section.workshopList = normalizeAttributesWorkshops(
          workshops?.data,
          translations,
          slugAnalyzed?.locale
        );
        section.workshop = undefined;
      }
      break;
    default:
      throw new Error(Errors.FORM_NOT_SUPPORTED);
  }
};

export default FormWorkshop;
